import React, { useState, useEffect, useCallback } from 'react';
import type { route, ILocationService, IWindowService } from 'angular';
import swal from 'sweetalert';

import { AlmanacController } from 'api';
import errorReporter from '@app/errorReporter';
import CalendarSyncProviderModal from '../DashboardComponents/CalendarSyncProviderModal';

interface ClientCalendarSyncButtonProps {
  clientType: 'seller' | 'buyer';
  $routeParams: route.IRouteParamsService;
  $location: ILocationService;
  $window: IWindowService;
  ToastService: any;
}

const ClientCalendarSyncButton: React.FC<ClientCalendarSyncButtonProps> = ({
  clientType,
  $routeParams,
  $location,
  $window,
  ToastService,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [openCalendarSyncModal, setOpenCalendarSyncModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [existAlmanacSubscription, setExistAlmanacSubscription] =
    useState(false);

  const { action, dashboardId, error: errorMessage, listingId } = $routeParams;

  useEffect(() => {
    if (errorMessage) {
      ToastService.add({
        message: `Could not sync calendar: ${errorMessage}`,
        type: 'danger',
      });
    }
    $location.search('error', null);
  }, [ToastService, errorMessage, $location]);

  const onCalendarSubscribeClick = useCallback(async () => {
    if (dashboardId) {
      try {
        if (clientType === 'seller') {
          await AlmanacController.subscribeToListing(dashboardId, listingId);
        } else {
          await AlmanacController.subscribeToBuyerShowings(dashboardId);
        }
        // We don't actually have the id of the AlmanacSubscription as it
        // requires Almanac to finish asynchronously processing it, but we
        // should still set the button to a synced state to disallow users
        // from spam-creating subscriptions for the same listing
        setExistAlmanacSubscription(true);
        swal({
          title: 'Calendar has been synced',
          type: 'success',
        });
      } catch (err) {
        setExistAlmanacSubscription(false);
        errorReporter.error(err);
        swal({
          title: 'Error: Failed to subscribe',
          type: 'error',
        });
      }
    }
  }, [clientType, dashboardId, listingId]);

  const onCalendarUnsubscribeClick = () => {
    swal(
      {
        title: 'Stop Calendar Syncing',
        text: `Are you sure you want to stop syncing appointments ${
          clientType === 'seller' ? 'on your property ' : ''
        }with your personal calendar?`,
        showCancelButton: true,
        type: 'warning',
      },
      async () => {
        try {
          if (clientType === 'seller') {
            await AlmanacController.unsubscribeFromListing(
              dashboardId,
              listingId,
            );
          } else {
            await AlmanacController.unsubscribeFromBuyerShowings(dashboardId);
          }
          setIsAuthenticated(false);
          setExistAlmanacSubscription(false);
        } catch (err) {
          errorReporter.error(err);
          swal({
            title: 'Error: Failed to unsubscribe',
            type: 'error',
          });
        }
      },
    );
  };

  const redirectToOauth = (service: string) => {
    const authConnectUrl = `/almanac/connect?service=${service}&dashboardId=${encodeURIComponent(
      dashboardId,
    )}&redirectUrl=${
      // This is pretty brittle, but URL doesn't play nice with angular's hash routing
      encodeURIComponent(`${$window.location.href}?action=subscribe`)
    }`;
    $window.location.href = authConnectUrl;
  };

  useEffect(() => {
    if (dashboardId) {
      (async () => {
        try {
          const almanacSubscriptionPromise =
            clientType === 'seller'
              ? AlmanacController.findAlmanacSellerSubscription(
                  dashboardId,
                  listingId,
                )
              : AlmanacController.findAlmanacBuyerSubscription(dashboardId);
          const [
            { isAuthenticated: _isAuthenticated },
            { id: subscriptionId },
          ] = await Promise.all([
            AlmanacController.isAuthenticated({ dashboardId }),
            almanacSubscriptionPromise,
          ]);

          setIsAuthenticated(_isAuthenticated);
          setExistAlmanacSubscription(!!subscriptionId);
          setIsLoaded(true);

          if (
            action === 'subscribe' &&
            _isAuthenticated &&
            !subscriptionId &&
            !errorMessage
          ) {
            $location.search('action', null);
            await onCalendarSubscribeClick();
          }
        } catch (err) {
          ToastService.add({
            message: `Almanac error: ${err.message}`,
            type: 'danger',
          });
        }
      })();
    }
  }, [
    $location,
    action,
    clientType,
    dashboardId,
    errorMessage,
    listingId,
    onCalendarSubscribeClick,
    ToastService,
  ]);

  return (
    <>
      {isLoaded && (
        <div>
          {existAlmanacSubscription ? (
            <button
              onClick={onCalendarUnsubscribeClick}
              className="btn btn-danger m-b-xs col-xs-12 col-lg-3"
            >
              <i className="fa fa-calendar-plus-o"></i>
              Stop Syncing to Calendar
            </button>
          ) : (
            <a
              onClick={() =>
                isAuthenticated
                  ? onCalendarSubscribeClick()
                  : setOpenCalendarSyncModal(true)
              }
              className="btn btn-info m-b-xs col-xs-12 col-lg-3"
            >
              <i className="fa fa-calendar-plus-o"></i>
              Sync Showings to Calendar
            </a>
          )}
        </div>
      )}
      <CalendarSyncProviderModal
        showModal={openCalendarSyncModal}
        redirectUser={redirectToOauth}
        closeModal={() => setOpenCalendarSyncModal(false)}
      />
    </>
  );
};

export default ClientCalendarSyncButton;
